import React, { useEffect } from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { sumBy } from "lodash"
import { numberToCurrency } from "helpers/string"

import { CATEGORY_ICONS } from "constants/accounting"
import { Icon } from "ui/Icon"
import FormAntdModal from "./FormAntdModal"
import { useSelector, useDispatch } from "react-redux"
import { getExpense, deleteExpense, getGuideAccounting } from "store/accounting"
import { requestParams } from "components/accounting/Header"
import { ConfirmModal } from "ui/ConfirmModal"
import { Avatar, Button, Card, Flex, Spin, Tag, Typography } from "antd"
import { colors } from "themes/variables"
import { useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { toaster } from "components/common/Toast"

const { Text } = Typography

DayDetails.propTypes = {
  date: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      class_name: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      displayed_name: PropTypes.string.isRequired,
      displayed_for: PropTypes.string,
      stripe: PropTypes.bool,
      slug: PropTypes.string
    })
  ).isRequired
}

LinkWrapperToEditExpence.propTypes = {
  record: PropTypes.shape({
    class_name: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }).isRequired,
  children: PropTypes.node.isRequired
}

function LinkWrapperToEditExpence({ record, children }) {
  const expense = useSelector((store) => store?.accounting?.expense)
  const dispatch = useDispatch()
  const [showModal, setShowModal] = React.useState(false)
  const prevIdRef = React.useRef()

  useEffect(() => {
    if (showModal && record?.id && prevIdRef.current !== record.id) {
      dispatch(getExpense(record.id))
      prevIdRef.current = record.id
    }
    if (!showModal) {
      prevIdRef.current = undefined
    }
  }, [showModal, record?.id, dispatch])

  if (record.class_name === "Expense") {
    return (
      <>
        <Button type="text" onClick={() => setShowModal(true)} style={{ padding: 0, height: "auto", flex: 1, textAlign: "left" }}>
          {children}
        </Button>
        {showModal && record?.id && (
          <FormAntdModal expenseId={record?.id} expense={expense} record={record} visible={showModal} setVisible={setShowModal} />
        )}
      </>
    )
  }
  return <div style={{ flex: 1 }}>{children}</div>
}

const getCategoryIcon = (slug) => CATEGORY_ICONS[slug] ?? "profit"

const getIconStyle = (record) => {
  if (record.class_name === "Income") {
    return record.amount >= 0 ? { backgroundColor: "#e6f4ff", color: "#1677ff" } : { backgroundColor: "#fff2f0", color: "#ff4d4f" }
  }
  return { backgroundColor: "#f0f0f0", color: "#52c41a" }
}

export default function DayDetails({ date, items = [] }) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [expenseToDelete, setExpenseToDelete] = React.useState(null)
  const [deletingId, setDeletingId] = React.useState(null)
  const [searchParams] = useSearchParams()

  const today = moment().startOf("day")
  const isToday = moment(date).diff(today, "days") === 0
  const totalAmount = sumBy(items, "amount")

  return (
    <Card styles={{ body: { padding: 0 } }} style={{ borderRadius: 8, overflow: "hidden" }}>
      <Flex justify="space-between" align="center" style={{ padding: "10px 15px", borderBottom: "1px solid #f0f0f0" }}>
        {date && (
          <Text strong style={{ color: isToday ? "var(--color-primary-second)" : undefined }}>
            {isToday ? t("expense.today") : moment(date).format("DD MMM")}
          </Text>
        )}
        <Text strong>{numberToCurrency(totalAmount)}</Text>
      </Flex>

      {items.length > 0 && (
        <Flex vertical>
          {items.map((record) => (
            <Flex key={record.id} align="center" gap={8} style={{ padding: "10px 15px" }}>
              <LinkWrapperToEditExpence record={record}>
                <Flex align="center" gap={8} style={{ width: "100%" }} wrap>
                  <Avatar icon={<Icon name={getCategoryIcon(record.slug)} />} style={getIconStyle(record)} size={36} />
                  <Flex vertical flex={1}>
                    <Text italic>{record.displayed_name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {record.displayed_for}
                    </Text>
                  </Flex>
                  <Flex>
                    <Tag color={record.stripe ? "blue" : "green"} style={{ marginRight: 4 }}>
                      {record.stripe ? t("expense.payment_card") : t("expense.payment_cash")}
                    </Tag>
                    <Text type={record.amount < 0 ? "danger" : undefined}>{numberToCurrency(record.amount)}</Text>
                  </Flex>
                </Flex>
              </LinkWrapperToEditExpence>

              {record.class_name === "Expense" &&
                (deletingId === record.id ? (
                  <Spin size="small" />
                ) : (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<Icon name="delete" width={14} height={14} fill={colors.error} />}
                    onClick={() => setExpenseToDelete(record)}
                  />
                ))}
            </Flex>
          ))}
        </Flex>
      )}

      <ConfirmModal
        open={expenseToDelete !== null}
        title={t("expense.delete_title")}
        info={`${t("expense.delete_confirmation")} <strong>${expenseToDelete?.displayed_name}</strong>? ${t("expense.delete_confirmation_suffix")}`}
        deleteLabel={t("expense.delete")}
        cancelLabel={t("expense.cancel")}
        onDelete={() => {
          const id = expenseToDelete?.id
          const params = Object.fromEntries(searchParams.entries())
          setExpenseToDelete(null)
          setDeletingId(id)
          dispatch(deleteExpense(id)).then(() => {
            dispatch(getGuideAccounting(requestParams(params)))
            setDeletingId(null)
            toaster.success(t("expense.deleted_success"))
          })
        }}
        onCancel={() => setExpenseToDelete(null)}
      />
    </Card>
  )
}
